import { getWindow } from "../../effects-bridge";
import { guid, callbackObj } from "./shared";

export * from "./shared";

/**
 * Uplifted pieces of `utils/nativeHooks` (apps/web 140 lines) — WEB half.
 *
 * Seeded with `isAndroid` (for `BusinessSlice.getBusinessDataObjSuccess`) plus the platform
 * detectors and `exec`, which `utils/OfflineUtil` needs. Still in the app: `sendDataToApp`,
 * `printbill` and `isAndroidOrSmDevice` (needs `commonHelpers`).
 *
 * ⚠️ When `printbill` is uplifted it must IMPORT the bill module
 * (`@dotpe/rista-library/lib/bill/bill.js`), not read the app's browser global for it — see
 * CLAUDE.md and ledger hazard 35. `pnpm audit:uplift` check K enforces this.
 *
 * ⚠️ **Mobile's `exec` is deliberately NOT here.** It is a different function — it calls
 * `ristadb.exec` through `utils/ristadb` (318 lines) — and nothing in core needs it: mobile's
 * `OfflineUtil` reaches the native layer through `execMobileServer`, and every `exec(` call in
 * that file is commented out. Lifting it would drag the native SQLite bridge in for no consumer.
 * `execMobileServer` is likewise still in the app — it needs `utils/localServer.fnServerExec`,
 * which imports back into `OfflineUtil`, so it moves WITH that pair (ledger, START HERE).
 *
 * 🔴 **`index.native.ts` now EXISTS — standing rule 4.** An earlier revision of this comment
 * argued the gap was deliberate. The observation behind it still holds and is preserved in the
 * native file: mobile's `isAndroid` is a genuinely DIFFERENT function (`return true;`, hardcoded,
 * because that file only ever runs inside the native app). What changed is the conclusion — a
 * file that exists in both apps gets all three core files, so the two `isAndroid` bodies now sit
 * in their own variants instead of one being absent.
 *
 * ⚠️ Mobile ALSO has an unrelated `utils/platform.js` `isAndroid` = `Platform.OS === 'android'`.
 * Three same-named functions, three different meanings. Do not "unify" them.
 */

/**
 * WEB. Truthy only when the web app is running inside the Android WebView wrapper.
 *
 * ⚠️ Returns the `ReactNativeWebView` OBJECT (or `undefined`), **not a boolean** — preserved
 * verbatim, because callers use it for truthiness and the app source is plain `.js`, so every
 * consumer sees `any` today. Narrowing the return to `boolean` would be a typecheck change the
 * uplift is not making.
 *
 * `window` -> `getWindow()`, the effects-bridge accessor, per `helpers/feedbackHelper`. Read
 * INSIDE the function, never at module scope (ledger hazard 18).
 */
export const isAndroid = (): any => {
  //fallback for opening on mobile browser added
  return getWindow()?.ReactNativeWebView;
};

/** WEB. True when running inside the Electron shell. */
export const isElectron = () => !!getWindow()?.electronAPI?.isElectron;

/** WEB. True when running inside the React Native WebView wrapper. */
export const isReactNative = () => !!getWindow()?.ReactNativeWebView;

/** WEB. True when embedded as an iframe under the legacy Sencha parent. */
export const isSenchaParent = () => {
  const w = getWindow();
  return !isElectron() && !isReactNative() && w?.parent !== w;
};

/** WEB. True in a plain browser tab — no Electron, no WebView, no parent frame. */
export const isStandaloneWeb = () => {
  const w = getWindow();
  return !isElectron() && !isReactNative() && w?.parent === w;
};

/**
 * WEB. Calls the native layer, by whichever route this shell provides, and returns the
 * correlation id.
 *
 * Three routes, and the branch order is load-bearing:
 *  1. **Electron** — direct IPC. With a `callbackFn` it is invoked `(err, data)`; WITHOUT one the
 *     result is re-dispatched as a synthetic `_platform_message` CustomEvent so the existing
 *     `receiveDataFromNW` listeners keep working unchanged.
 *  2. **Standalone browser** — no native layer, so any `callbackFn` is fired ASYNCHRONOUSLY with
 *     `ERR_PLATFORM_UNAVAILABLE`. The `setTimeout` matters: callers must not be re-entered
 *     synchronously.
 *  3. **Sencha iframe / RN WebView** — the original `parent.postMessage` wire format, with the
 *     callback parked in `callbackObj[id]` for `OfflineUtil.handleGetCallback` to resolve.
 *
 * ⚠️ Note routes 1 and 2 return the id EARLY; only route 3 falls through to the bottom `return`.
 * ⚠️ The nested try/catch around `postMessage` is a real cross-origin guard, not redundancy.
 * ⚠️ `window`/`parent` become `getWindow()` / `getWindow()?.parent`, read INSIDE the function —
 * never at module scope (ledger hazard 18).
 */
export const exec = function (key: any, action: any, args?: any, params?: any) {
  console.log(">_< ==> exec", key, action, args, params);
  var id = guid();
  const w = getWindow();

  if (isElectron()) {
    // Direct IPC — no postMessage bridge needed
    w.electronAPI
      .exec(key, action, args)
      .then(function (result: any) {
        if (params && params.callbackFn) {
          // Typed callback path (explicit callbackFn passed by caller)
          var err = result && result.error ? result.error : null;
          var data = result && (result.success !== undefined ? result.success : result.data);
          params.callbackFn(err, data);
        } else {
          // No callback — dispatch a synthetic _platform_message so existing
          // receiveDataFromNW listeners handle the result without any changes.
          // In Electron, window.parent === window, so parent.addEventListener
          // and window.addEventListener are the same target.
          var syntheticAction = result && result.action ? result.action : action;
          w.dispatchEvent(
            new CustomEvent("_platform_message", {
              detail: { key: key, action: syntheticAction, message: result || {} },
            }),
          );
        }
      })
      .catch(function (err: any) {
        if (params && params.callbackFn) {
          params.callbackFn(err && err.message ? err.message : "ERR_IPC_FAILED");
        }
      });
    return id;
  }

  if (isStandaloneWeb()) {
    // No native layer available — fire callback async so callers don't hang
    if (params && params.callbackFn) {
      setTimeout(function () {
        params.callbackFn("ERR_PLATFORM_UNAVAILABLE");
      }, 0);
    }
    return id;
  }

  // Sencha iframe / React Native WebView — existing postMessage wire format (unchanged)
  try {
    if (params?.callbackFn && !callbackObj[id]) {
      callbackObj[id] = {
        callback: params.callbackFn,
      };
    }
    // ✅ Safe cross-origin error catch
    try {
      w?.parent?.postMessage(
        {
          detail: {
            key: key,
            action: action,
            message: {
              id: id,
              args: args,
            },
          },
        },
        "*",
      );
    } catch (err: any) {
      console.warn("[Cross-Origin Warning]", err?.message);
    }
  } catch (error) {
    console.log("nativeHooks ==> exec");
    console.log(error);
  }

  return id;
};
